import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowLeft, CheckCircle2, ShieldAlert } from "lucide-react";

const articles = [
  {
    title: "Commercial Cleaning Quality Control: 100-Point Inspection SOP",
    excerpt:
      "Implement standardized inspection checklists, photo-verified corrective action plans, and quality metrics to maintain 98%+ client retention.",
    readTime: 7,
    href: "/success-hub/article/janitorial-quality-control-inspection-sop",
  },
  {
    title: "How to Handle Commercial Cleaning Client Complaints: 2-Hour SLA Protocol",
    excerpt:
      "A proven protocol to handle commercial cleaning complaints, resolve facility manager issues within 2 hours, and turn unhappy accounts into 5-star references.",
    readTime: 6,
    href: "/success-hub/article/handling-commercial-cleaning-complaints",
  },
  {
    title: "Commercial Cleaning Client Retention Playbook: Stopping Silent Churn",
    excerpt:
      "Proactive communication strategies, touchpoint calendars, and automated review collection loops that stop gradual account degradation.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-client-retention-strategies",
  },
  {
    title: "Janitorial Corrective Action Plan (CAP) Template & Root Cause Analysis",
    excerpt:
      "Step-by-step framework for delivering formal CAP documents with 5-Why root cause analysis and photo-verified remediation within 24 hours.",
    readTime: 7,
    href: "/success-hub/article/janitorial-corrective-action-plan-template",
  },
  {
    title: "Service Level Agreements (SLAs) for Commercial Cleaning Contracts",
    excerpt:
      "How to define response times, inspection score thresholds, re-clean credits, and cure notice periods in commercial janitorial contracts.",
    readTime: 8,
    href: "/success-hub/article/commercial-cleaning-sla-service-level-agreements",
  },
  {
    title: "Commercial Restroom Sanitation & Odor Control Inspection Checklist",
    excerpt:
      "Detailed SOP for eliminating commercial restroom odors, preventing grout bacterial buildup, and passing high-traffic facility audits.",
    readTime: 6,
    href: "/success-hub/article/restroom-sanitation-inspection-checklist",
  },
  {
    title: "APPA Cleaning Standards & Janitorial Quality Assurance Levels",
    excerpt:
      "Understanding APPA Levels 1 through 5 (Orderly Spotlessness to Unkempt Neglect) and how to set appropriate custodial benchmark levels.",
    readTime: 8,
    href: "/success-hub/article/janitorial-quality-assurance-scoring-rubric",
  },
  {
    title: "How to Run Commercial Cleaning Quarterly Business Reviews (QBR)",
    excerpt:
      "The executive meeting agenda to review SLA performance with property managers, recommend floor care add-ons, and secure contract renewals.",
    readTime: 6,
    href: "/success-hub/article/commercial-cleaning-quarterly-business-reviews",
  },
];

export default function QualityRetentionPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Quality Control & Client Retention | Commercial Cleaning SOPs"
        description="Proven quality control inspection checklists, complaint resolution SOPs, Corrective Action Plans (CAP), and retention playbooks for commercial cleaning contractors."
        keywords={[
          "commercial cleaning quality control",
          "janitorial inspection checklist",
          "cleaning complaint resolution SOP",
          "janitorial client retention",
          "APPA cleaning standards",
        ]}
        url="/success-hub/quality-retention"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "Quality Control & Retention", url: "/success-hub/quality-retention" },
          ]),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28">
        <div className="container mx-auto px-4">
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-6"
          >
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-amber-100">
              <CheckCircle2 className="h-8 w-8 text-amber-600" />
            </div>
            <div>
              <h1 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">
                Quality Control & Client Retention
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                Inspection rubrics, 2-hour complaint resolution protocols, Corrective Action Plans (CAP), and retention playbooks to eliminate contract churn.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles List */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              {articles.map((article, index) => (
                <ArticleCard
                  key={index}
                  title={article.title}
                  excerpt={article.excerpt}
                  category="Quality & Retention"
                  categoryColor="bg-amber-100 text-amber-700"
                  readTime={article.readTime}
                  href={article.href}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
